/**
 * VAD (Voice Activity Detection) 状态机 Hook
 *
 * 单阶段模式（全程 getUserMedia 一路到底）：
 *
 *   idle → recording(侦听) → speaking(录入+录音) → silence_detecting(结束录音) → recording
 *
 * 流程：
 *   1. getUserMedia 获取麦克风
 *   2. AudioContext/AnalyserNode → 实时波形 + 音量检测
 *   3. MediaRecorder → 检测到说话时开始录制，静音后停止并输出 Blob
 */
import { useState, useRef, useCallback, useEffect } from "react";
import type {
  VadState,
  VadRecorderConfig,
  UseVadRecorderReturn,
  DeviceStatus,
  MicErrorType,
} from "./types";
import { getAudioMonitor } from "./audio-monitor";
// 延迟导入避免循环依赖（VoiceRuntime 导出这些函数）
let _startLocalSTT: (() => boolean) | null = null;
let _abortLocalSTT: (() => void) | null = null;
let _markLocalSTT: (() => void) | null = null;

/** 由 VoiceRuntime 在运行时注入 SpeechRecognition 控制函数 */
export function injectLocalSTTControls(startFn: () => boolean, abortFn: () => void, markFn?: () => void): void {
  _startLocalSTT = startFn;
  _abortLocalSTT = abortFn;
  _markLocalSTT = markFn || null;
}

export function useVadRecorder(
  config: VadRecorderConfig,
): UseVadRecorderReturn {
  const silenceMs = config.silenceDurationMs ?? 2000;

  // ── 参数 ──
  const SPEAK_THRESHOLD = config.silenceThreshold ?? 30;
  /** ★ 软静默阈值：低于此值说明人声已停（但可能有回声/底噪残留） */
  const SOFT_SILENCE_THRESHOLD = Math.max(SPEAK_THRESHOLD * 1.8, 18);
  const MIN_SPEAK_MS = 1000;
  const WINDOW_SIZE = 10;
  /** 从尾音倒计时恢复说话后的短暂保护，避免电平窗仍全是 0 而立刻再次进入尾音判定 */
  const RESUME_GRACE_MS = 550;
  /** 最大录制时长（ms），超过后强制收尾，防止 VAD 静音检测失效时卡死 */
  const MAX_RECORDING_MS = 15000;
  /**
   * ★ 软静默最大容忍时长(ms)
   * 手机不戴耳机时回声/环境噪让 voiceLevel 持续在 10~25 区间，
   * 无法触发硬静默(<=SPEAK_THRESHOLD)，用此机制兜底。
   */
  const MAX_SOFT_SILENCE_MS = 3500;

  // ── State ──
  const [vadState, setVadState] = useState<VadState>("idle");
  const [audioLevel, setAudioLevel] = useState(0);
  const [silenceCountdown, setSilenceCountdown] = useState(0);
  const [deviceStatus, setDeviceStatus] = useState<DeviceStatus>("checking");
  /** 是否处于静音状态（控制波形显示） */
  const [audioSilent, setAudioSilent] = useState(true);
  /** 录音结束、Blob 已输出，等待外部处理完成 */
  const [isAudioProcessing, setIsAudioProcessing] = useState(false);

  // ── 设备检测 ──
  useEffect(() => {
    const hasMedia = !!navigator.mediaDevices?.getUserMedia;
    setDeviceStatus(hasMedia ? "available" : "denied");
  }, []);

  // ── Refs ──
  const stateRef = useRef<VadState>("idle");
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const speakStartTimeRef = useRef(0);
  const levelWindowRef = useRef<number[]>([]);
  const monitorTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const waveDataRef = useRef<Uint8Array>(new Uint8Array(0));
  /** 续说保护截止时间（performance.now） */
  const resumeGraceUntilRef = useRef(0);
  /** 尾音倒计时调度代数，用于忽略已被取消的计时回调 */
  const silenceScheduleGenRef = useRef(0);
  /** 软静默开始时间（Date.now），用于检测持续低能量（回声/底噪场景） */
  const softSilenceStartRef = useRef(0);
  /**
   * ★ 按住录音启动中标记：startPressToRecord 在 await monitor.start()（首次授权）期间，
   *   用户可能已松手。此时 stopPressToRecord 因 state 尚未到 press_recording 会提前返回，
   *   导致状态卡死"一直聆听中"。该标记用于记录"启动中已请求停止"，启动完成后立即收尾。
   */
  const pressStopRequestedRef = useRef(false);
  /** 启动中是否携带取消标记（cancel vs 正常发送） */
  const pressStopCancelledRef = useRef(false);
  /** 按住录音启动流程是否进行中（await 麦克风授权期间为 true） */
  const pressStartingRef = useRef(false);

  useEffect(() => { stateRef.current = vadState; }, [vadState]);

  const clearSilenceTimer = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    setSilenceCountdown(0);
  }, []);

  const transition = useCallback(
    (next: VadState) => {
      console.log(`[VAD] ${stateRef.current} → ${next}`);
      stateRef.current = next;
      setVadState(next);
      if (next !== "silence_detecting") clearSilenceTimer();
    },
    [clearSilenceTimer],
  );

  /**
   * ★ 结束录制：停止 MediaRecorder → 获取 Blob → 回调输出 → 回到侦听状态
   */
  const finishRecording = useCallback(async () => {
    if (stateRef.current !== "silence_detecting") {
      console.log("[VAD] finishRecording 已取消（状态已变，可能用户在收尾阶段续说）");
      return;
    }
    const monitor = getAudioMonitor();

    // 停止 MediaRecorder 并获取 Blob（单段连续录制，含中间停顿与续说）
    const blob = await monitor.stopRecording();

    if (blob && blob.size > 100) {
      console.log(`[VAD] 录音完成, size: ${blob.size} bytes, type: ${blob.type}`);
      setIsAudioProcessing(true);
      config.onAudioRecorded?.(blob);
    } else {
      console.log("[VAD] 录音为空或过小，丢弃", blob ? `size=${blob.size}` : "null");
      // ★ 通知外部录音无效，让 UI 层可以给用户提示
      config.onError?.("device_error", "录音太短或未采集到声音，请重试");
    }

    transition("recording");
    speakStartTimeRef.current = 0;
    levelWindowRef.current = [];
    resumeGraceUntilRef.current = 0;
    softSilenceStartRef.current = 0;
    setAudioLevel(0);
    setAudioSilent(true);
  }, [config, transition]);

  const startSilenceCountdown = useCallback(() => {
    clearSilenceTimer();
    const scheduleGen = ++silenceScheduleGenRef.current;
    const start = Date.now();
    tickRef.current = setInterval(() => {
      const remaining = Math.max(0, silenceMs - (Date.now() - start));
      setSilenceCountdown(remaining);
      if (remaining <= 0 && tickRef.current) clearInterval(tickRef.current);
    }, 50);
    silenceTimerRef.current = setTimeout(() => {
      if (scheduleGen !== silenceScheduleGenRef.current) return;
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
      if (stateRef.current !== "silence_detecting") return;
      void finishRecording();
    }, silenceMs);
  }, [clearSilenceTimer, finishRecording, silenceMs]);

  /**
   * ★ 音频轮询（~20次/秒）
   * - 驱动实时波形数据
   * - 驱动 VAD 状态机
   * - 控制 MediaRecorder 录制启停
   */
  const pollAudio = useCallback(() => {
    const monitor = getAudioMonitor();
    if (!monitor.isRunning) return;

    const voiceLevel = monitor.getVoicePowerLevel();
    const rawLevel = monitor.getPowerLevel();
    const maxDev = monitor.getMaxDeviation();
    const RAW_SILENT_THRESHOLD = 3;
    const isRawSilent = maxDev < RAW_SILENT_THRESHOLD;

    // ★ 始终更新波形数据（给 WaveViewCanvas 用）
    setAudioSilent(isRawSilent);
    setAudioLevel(isRawSilent ? 0 : rawLevel);
    waveDataRef.current = isRawSilent
      ? new Uint8Array(
          monitor.analyser ? monitor.analyser.frequencyBinCount : 0,
        ).fill(128)
      : monitor.getTimeDomainData();

    const powerLevel = isRawSilent ? 0 : voiceLevel;
    const state = stateRef.current;

    // ★ 只在收录/说话/静音倒计时状态做 VAD 判断
    if (state !== "recording" && state !== "speaking" && state !== "silence_detecting") return;

    const win = levelWindowRef.current;
    win.push(powerLevel);
    if (win.length > WINDOW_SIZE) win.shift();

    const windowMax = Math.max(...win, 0);
    const isFrameSilent = powerLevel <= SPEAK_THRESHOLD;

    if (state === "recording") {
      if (windowMax > SPEAK_THRESHOLD) {
        // ★ 检测到说话 → 开始录制
        speakStartTimeRef.current = Date.now();
        setAudioSilent(false);

        // 启动 MediaRecorder
        monitor.startRecording();
        // ★ 标记截断点：从此刻起只保留新的转写结果（去除之前的环境噪音识别）
        _markLocalSTT?.();
        console.log(
          `[VAD] 开始录音 voiceLevel:${voiceLevel.toFixed(1)} rawLevel:${rawLevel.toFixed(1)} maxDev:${maxDev}`,
        );
        softSilenceStartRef.current = 0;
        transition("speaking");
      }
    } else if (state === "speaking") {
      const spokenMs = Date.now() - speakStartTimeRef.current;
      const inResumeGrace = performance.now() < resumeGraceUntilRef.current;

      // ★ 最大录制时长保护：防止 VAD 静音检测失效时卡死
      if (spokenMs > MAX_RECORDING_MS) {
        console.warn(`[VAD] 录音超过 ${MAX_RECORDING_MS}ms，强制收尾`);
        transition("silence_detecting");
        startSilenceCountdown();
        return;
      }

      // ★ 软静默检测：手机不戴耳机时回声/底噪让 voiceLevel 持续在中低区间，
      //   无法触发硬静默(<=SPEAK_THRESHOLD)，用此机制兜底。
      if (powerLevel < SOFT_SILENCE_THRESHOLD) {
        if (softSilenceStartRef.current === 0) {
          softSilenceStartRef.current = Date.now();
        }
        const softSilenceMs = Date.now() - softSilenceStartRef.current;
        if (softSilenceMs > MAX_SOFT_SILENCE_MS) {
          console.warn(
            `[VAD] 软静默超时(${softSilenceMs}ms), voiceLevel=${voiceLevel.toFixed(1)}, 阈值=${SPEAK_THRESHOLD}, 强制收尾`,
          );
          softSilenceStartRef.current = 0;
          transition("silence_detecting");
          startSilenceCountdown();
          return;
        }
      } else {
        // 能量回升，重置软静默计时
        softSilenceStartRef.current = 0;
      }

      // 在 speaking 阶段，一旦检测到硬静默就立刻开始 2s 计时，避免窗口二次延迟。
      if (isFrameSilent) {
        // 刚从尾音倒计时续说：电平窗可能仍满载 0，短暂忽略静默以免立刻再次收尾
        if (inResumeGrace) {
          return;
        }
        if (spokenMs < MIN_SPEAK_MS) {
          // ★ 说话太短，取消录制
          console.log(
            `[VAD] <${MIN_SPEAK_MS}ms 舍弃, 时长:${spokenMs}ms`,
          );
          monitor.cancelRecording();
          _abortLocalSTT?.(); // ★ 语音太短时中止语音识别
          speakStartTimeRef.current = 0;
          levelWindowRef.current = [];
          softSilenceStartRef.current = 0;
          setAudioLevel(0);
          setAudioSilent(true);
          transition("recording");
        } else {
          // ★ 进入静音倒计时
          transition("silence_detecting");
          startSilenceCountdown();
        }
      }
    } else if (state === "silence_detecting") {
      // ★ 倒计时期间检测到用户又说话了 → 取消倒计时，回到 speaking（同一段录音不停止）
      if (!isFrameSilent) {
        silenceScheduleGenRef.current += 1;
        clearSilenceTimer();
        resumeGraceUntilRef.current = performance.now() + RESUME_GRACE_MS;
        levelWindowRef.current = [];
        console.log(`[VAD] 倒计时中检测到说话, 取消倒计时, 回到 speaking`);
        transition("speaking");
        softSilenceStartRef.current = 0;
      }
    }
  }, [
    SPEAK_THRESHOLD,
    WINDOW_SIZE,
    MIN_SPEAK_MS,
    transition,
    startSilenceCountdown,
    clearSilenceTimer,
  ]);

  /**
   * ★ 启动录音监听（VAD 自动检测模式）
   */
  const startListening = useCallback(async () => {
    if (stateRef.current !== "idle") return;

    try {
      // 启动 AudioMonitor（获取麦克风 + 创建 AnalyserNode + MediaRecorder）
      const monitor = getAudioMonitor();
      const audioStarted = await monitor.start();

      if (!audioStarted) {
        const errMsg = "无法获取麦克风权限，请检查浏览器麦克风设置";
        console.warn("[VAD]", errMsg);
        config.onError?.("permission_denied", errMsg);
        transition("idle");
        return;
      }

      levelWindowRef.current = [];
      speakStartTimeRef.current = 0;
      resumeGraceUntilRef.current = 0;
      softSilenceStartRef.current = 0;
      setAudioLevel(0);
      setAudioSilent(true);

      transition("recording");

      // ★ 启动音频轮询（波形 + VAD + 录制控制）
      if (monitorTimerRef.current) clearInterval(monitorTimerRef.current);
      monitorTimerRef.current = setInterval(pollAudio, monitor.pollIntervalMs);

      // ★ 同步启动浏览器原生语音识别（必须在用户交互上下文中调用，兼容 iOS Safari）
      _startLocalSTT?.();

      console.log("[VAD] 开始监听麦克风");
    } catch (e) {
      const errStr = String(e);
      let errorType: MicErrorType = "unknown";
      if (errStr.includes("NotAllowed") || errStr.includes("Permission")) {
        errorType = "permission_denied";
      } else if (errStr.includes("NotFoundError") || errStr.includes("DevicesNotFound")) {
        errorType = "not_found";
      }
      const errMsg = `启动失败: ${errStr}`;
      console.error(`[VAD] ${errMsg}`);
      config.onError?.(errorType, errMsg);
      transition("idle");
    }
  }, [pollAudio, transition]);

  // ── ★ 按住录音模式（微信式：按住即录、松手即停）──

  /** 松手停止：结束录制 → 输出 Blob → 回到 idle。返回 true 表示确实进入了录音停止流程（会有输出或取消动作）*/
  const stopPressToRecord = useCallback(async (cancelled = false): Promise<boolean> => {
    // ★ 若尚未进入 press_recording（startPressToRecord 正在 await 麦克风授权）：
    //   记录停止请求，由 startPressToRecord 启动完成后立即收尾，避免"一直聆听中"。
    if (stateRef.current !== "press_recording") {
      // 仅当确实处于"启动中"（正在录音启动流程）时记录；完全空闲态直接忽略
      if (pressStartingRef.current) {
        pressStopRequestedRef.current = true;
        pressStopCancelledRef.current = cancelled;
        console.log(`[VAD] 启动中收到停止请求(cancelled=${cancelled}), 等待启动完成立即收尾`);
        return true; // 启动完成后会正常收尾（发送或取消）
      }
      return false; // 完全空闲态（未录音、未在启动），没有实际停止动作
    }

    const monitor = getAudioMonitor();

    // 停止波形轮询
    if (monitorTimerRef.current) {
      clearInterval(monitorTimerRef.current);
      monitorTimerRef.current = null;
    }

    if (monitor.isRecording) {
      if (cancelled) {
        monitor.cancelRecording();
        _abortLocalSTT?.(); // ★ 取消录音时中止语音识别
        console.log("[VAD] 按住录音已取消");
      } else {
        const blob = await monitor.stopRecording();
        if (blob && blob.size > 100) {
          console.log(`[VAD] 按住录音完成, size: ${blob.size} bytes`);
          setIsAudioProcessing(true);
          config.onAudioRecorded?.(blob);
        } else {
          console.log("[VAD] 录音为空或过小，丢弃", blob ? `size=${blob.size}` : "null");
          config.onError?.("device_error", "录音太短或未采集到声音，请重试");
        }
      }
    }

    transition("idle");
    speakStartTimeRef.current = 0;
    levelWindowRef.current = [];
    softSilenceStartRef.current = 0;
    setAudioLevel(0);
    setAudioSilent(true);

    // 关闭麦克风释放资源（每次按住都重新申请，确保干净状态）
    monitor.stop();
    return true; // 已进入 press_recording 并执行了停止动作
  }, [config, transition]);

  /** 按住开始：获取麦克风权限 + 立即开始录制 */
  const startPressToRecord = useCallback(async (): Promise<boolean> => {
    // 如果已在录音中，不允许重复启动
    if (stateRef.current === "press_recording") return true;

    // ★ 重置启动中停止标记（新一轮按住）
    pressStopRequestedRef.current = false;
    pressStopCancelledRef.current = false;
    pressStartingRef.current = true;

    try {
      const monitor = getAudioMonitor();

      // 首次需要初始化麦克风（可能弹出权限框，属异步等待）
      if (!monitor.isRunning) {
        const ok = await monitor.start();
        if (!ok) {
          pressStartingRef.current = false;
          config.onError?.("permission_denied", "无法获取麦克风权限");
          transition("idle");
          return false;
        }
      }

      levelWindowRef.current = [];
      setAudioLevel(0);
      setAudioSilent(true);

      // 立即进入 press_recording 状态并开始录制
      transition("press_recording");
      monitor.startRecording();
      // ★ 同步启动浏览器原生语音识别（获取转写文字，兼容 iOS）
      _startLocalSTT?.();

      // 启动波形轮询（仅用于波形显示，不做 VAD 判断）
      if (monitorTimerRef.current) clearInterval(monitorTimerRef.current);
      monitorTimerRef.current = setInterval(pollWaveformOnly, monitor.pollIntervalMs);
      // 启动流程完成（无论是否收到停止请求都清除标记）
      pressStartingRef.current = false;

      console.log("[VAD] 按住录音已开始");

      // ★ 启动期间用户已松手（stopPressToRecord 提前 return 了）→ 立即收尾，避免"一直聆听中"
      if (pressStopRequestedRef.current) {
        console.log("[VAD] 启动期间已请求停止，立即收尾");
        const cancelled = pressStopCancelledRef.current;
        pressStopRequestedRef.current = false;
        void stopPressToRecord(cancelled);
      }

      return true;
    } catch (e) {
      pressStartingRef.current = false;
      const errStr = String(e);
      let errorType: MicErrorType = "unknown";
      if (errStr.includes("NotAllowed") || errStr.includes("Permission")) errorType = "permission_denied";
      else if (errStr.includes("NotFoundError")) errorType = "not_found";
      config.onError?.(errorType, `启动失败: ${errStr}`);
      transition("idle");
      return false;
    }
  }, [transition, config, stopPressToRecord]);

  /**
   * 仅轮询波形数据（不执行 VAD 判断），用于按住录音模式的实时波形显示
   */
  const pollWaveformOnly = useCallback(() => {
    const monitor = getAudioMonitor();
    if (!monitor.isRunning || stateRef.current !== "press_recording") return;

    const rawLevel = monitor.getPowerLevel();
    const maxDev = monitor.getMaxDeviation();
    const RAW_SILENT_THRESHOLD = 3;
    const isRawSilent = maxDev < RAW_SILENT_THRESHOLD;

    setAudioSilent(isRawSilent);
    setAudioLevel(isRawSilent ? 0 : rawLevel);
    waveDataRef.current = isRawSilent
      ? new Uint8Array(monitor.analyser ? monitor.analyser.frequencyBinCount : 0).fill(128)
      : monitor.getTimeDomainData();
  }, []);

  /** 停止监听 */
  const stopListening = useCallback(() => {
    clearSilenceTimer();
    if (monitorTimerRef.current) {
      clearInterval(monitorTimerRef.current);
      monitorTimerRef.current = null;
    }

    const monitor = getAudioMonitor();
    if (monitor.isRecording) {
      monitor.cancelRecording();
    }
    // ★ 停止监听时同步中止语音识别
    _abortLocalSTT?.();
    monitor.stop();

    setAudioLevel(0);
    setAudioSilent(true);
    speakStartTimeRef.current = 0;
    levelWindowRef.current = [];
    resumeGraceUntilRef.current = 0;
    softSilenceStartRef.current = 0;
    silenceScheduleGenRef.current += 1;
    transition("idle");
  }, [clearSilenceTimer, transition]);

  /** 外部处理完成后调用，重置 isAudioProcessing */
  const finishAudioProcessing = useCallback(() => setIsAudioProcessing(false), []);

  useEffect(() => {
    return () => {
      clearSilenceTimer();
      if (monitorTimerRef.current) {
        clearInterval(monitorTimerRef.current);
        monitorTimerRef.current = null;
      }
      getAudioMonitor().destroy();
    };
  }, [clearSilenceTimer]);

  const isListening =
    vadState === "recording" ||
    vadState === "speaking" ||
    vadState === "silence_detecting" ||
    vadState === "press_recording";

  /** 是否处于按住录音模式 */
  const isPressRecording = vadState === "press_recording";

  return {
    vadState,
    isListening,
    isPressRecording,
    audioLevel,
    audioSilent,
    silenceCountdown,
    startListening,
    stopListening,
    startPressToRecord,
    stopPressToRecord,
    deviceStatus,
    get waveData(): Uint8Array {
      return waveDataRef.current;
    },
    isModelLoading: false,
    isModelReady: true,
    isAudioProcessing,
    finishAudioProcessing,
  };
}
